import { create } from 'zustand'
import { useEffect } from 'react'
import { getAllDrafts, getSubmissions } from '@/lib/db'
import type { UploadProgressEventDetail } from '@/lib/uploadEvents'
import { UPLOAD_PROGRESS_EVENT } from '@/lib/uploadEvents'

interface UploadProgressState {
  queuedCount: number
  syncedCount: number
  isUploading: boolean
  uploadPct: number
  currentSessionId: string | null
  finalizePhase: 'idle' | 'pending' | 'complete'
  finalizeSessionId: string | null
}

const baseState: UploadProgressState = {
  queuedCount: 0,
  syncedCount: 0,
  isUploading: false,
  uploadPct: 0,
  currentSessionId: null,
  finalizePhase: 'idle',
  finalizeSessionId: null,
}

export const getUploadProgressInitialState = (): UploadProgressState => ({ ...baseState })

export const useUploadProgressStore = create<UploadProgressState>(() => getUploadProgressInitialState())

let listenersInitialized = false
let refreshInFlight: Promise<void> | null = null

async function refreshCounts(): Promise<void> {
  if (refreshInFlight) {
    return refreshInFlight
  }

  refreshInFlight = (async () => {
    const [submissions, drafts] = await Promise.all([getSubmissions(), getAllDrafts()])
    const mergedStatusById = new Map<string, { isDraft: boolean; syncedAt: string | null }>()
    for (const draft of drafts) {
      mergedStatusById.set(draft.id, { isDraft: true, syncedAt: null })
    }
    for (const submission of submissions) {
      mergedStatusById.set(submission.id, { isDraft: false, syncedAt: submission.syncedAt })
    }

    let syncedCount = 0
    let queuedCount = 0
    for (const item of mergedStatusById.values()) {
      if (!item.isDraft && Boolean(item.syncedAt)) {
        syncedCount += 1
      } else {
        queuedCount += 1
      }
    }

    useUploadProgressStore.setState((state) => {
      if (state.queuedCount === queuedCount && state.syncedCount === syncedCount) {
        return state
      }
      return {
        ...state,
        queuedCount,
        syncedCount,
      }
    })
  })()

  try {
    await refreshInFlight
  } finally {
    refreshInFlight = null
  }
}

function handleRecordingMutation(): void {
  void refreshCounts()
}

function handleUploadProgressEvent(event: CustomEvent<UploadProgressEventDetail>): void {
  const detail = event.detail
  if (!detail) {
    return
  }

  if (detail.type === 'start') {
    useUploadProgressStore.setState((state) => ({
      ...state,
      isUploading: true,
      uploadPct: 0,
      currentSessionId: detail.sessionId ?? state.currentSessionId,
    }))
    return
  }

  if (detail.type === 'finalize') {
    useUploadProgressStore.setState((state) => ({
      ...state,
      finalizePhase: detail.phase,
      finalizeSessionId:
        detail.phase === 'idle' ? null : (detail.sessionId ?? state.finalizeSessionId),
    }))
    return
  }

  if (detail.type === 'progress') {
    useUploadProgressStore.setState((state) => ({
      ...state,
      isUploading: true,
      uploadPct: detail.pct,
      currentSessionId: detail.sessionId ?? state.currentSessionId,
    }))
    return
  }

  if (detail.type === 'complete') {
    useUploadProgressStore.setState((state) => ({
      ...state,
      isUploading: false,
      uploadPct: 100,
      currentSessionId: null,
      finalizePhase:
        detail.total > 0 && state.finalizePhase === 'pending' ? 'complete' : state.finalizePhase,
    }))
    void refreshCounts()
    return
  }

  if (detail.type === 'error') {
    useUploadProgressStore.setState((state) => ({
      ...state,
      isUploading: false,
    }))
    void refreshCounts()
    return
  }

  if (detail.type === 'refresh') {
    void refreshCounts()
  }
}

function initUploadProgressListeners(): void {
  if (listenersInitialized || typeof window === 'undefined') {
    return
  }

  window.addEventListener('recording-saved', handleRecordingMutation)
  window.addEventListener(UPLOAD_PROGRESS_EVENT, handleUploadProgressEvent as EventListener)
  listenersInitialized = true
}

export function initializeUploadProgressTracking(): void {
  initUploadProgressListeners()
}

export function useUploadProgress(): UploadProgressState & { safeToClose: boolean } {
  const state = useUploadProgressStore()

  useEffect(() => {
    initUploadProgressListeners()
    void refreshCounts()
  }, [])

  const safeToClose =
    !state.isUploading &&
    state.queuedCount === 0 &&
    state.finalizePhase !== 'pending'

  return { ...state, safeToClose }
}

export async function forceUploadProgressRefresh(): Promise<void> {
  await refreshCounts()
}
